import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'effector-react';
import { Button } from 'antd';
import ComplaintsReasonsList from '../ComplaintsReasonsList/ComplaintsReasonsList';
import IComment from '@interfaces/IComment';
import IPost from '@interfaces/IPost';
import ComplaintsList from '@components/ComplaintsList';
import useTranslate from '@hooks/useTranslate';
import componentContent from './SpamPanel.conent.json';
import css from './SpamPanel.module.less';
import useGuards from '@hooks/useGuards';
import spamComplainers, {
  getList as getListComplainers,
  reset as resetComplainers,
} from '@stores/spam-complainers';
import request from '@helpers/request';
import api from '@constants/endpoints';

interface ComplaintReason {
  id: number;
  type?: string;
  text?: string;
}

const complaintReasonsCache = new Map<string, ComplaintReason[]>();
const complaintReasonsRequests = new Map<string, Promise<ComplaintReason[]>>();
const MAX_COMPLAINT_REASONS_CACHE_SIZE = 500;

const getComplaintReasonsCacheKey = (
  itemId: IPost['id'] | IComment['id'],
  mode: 'post' | 'comment',
) => `${mode}:${itemId}`;

const fetchComplaintReasons = ({
  itemId,
  mode,
}: {
  itemId: IPost['id'] | IComment['id'];
  mode: 'post' | 'comment';
}) => {
  const cacheKey = getComplaintReasonsCacheKey(itemId, mode);
  const cachedReasons = complaintReasonsCache.get(cacheKey);
  if (cachedReasons) return Promise.resolve(cachedReasons);

  const activeRequest = complaintReasonsRequests.get(cacheKey);
  if (activeRequest) return activeRequest;

  const endpoint =
    mode === 'comment' ? api.spam.getCommentComplaintReasons : api.spam.getPostComplaintReasons;
  const requestPromise = request(endpoint, {
    urlParams: { itemId },
    params: {
      paged: false,
      sort: 'id,desc',
    },
  })
    .then((response) => {
      const reasons = (response.data.body.result || []) as ComplaintReason[];

      if (complaintReasonsCache.size >= MAX_COMPLAINT_REASONS_CACHE_SIZE) {
        const oldestCacheKey = complaintReasonsCache.keys().next().value;
        if (oldestCacheKey) complaintReasonsCache.delete(oldestCacheKey);
      }

      complaintReasonsCache.set(cacheKey, reasons);
      return reasons;
    })
    .finally(() => {
      complaintReasonsRequests.delete(cacheKey);
    });

  complaintReasonsRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

interface SpamPanelProps {
  onHidePost?: () => void;
  isHidingPost?: boolean;
  onUnhidePost?: () => void;
  isUnhidingPost?: boolean;
  onBlock: () => void;
  isBlocking: boolean;
  onUnblock: () => void;
  isUnblocking: boolean;
  onFacePost?: () => void;
  isSettingFacePost?: boolean;
  onRemovefacePost?: () => void;
  isRemovingFacePost?: boolean;
  item: IPost | IComment;
  mode: 'post' | 'comment';
  disabled?: boolean;
}

const SpamPanel: FunctionComponent<SpamPanelProps> = ({
  onHidePost,
  isHidingPost,
  onUnhidePost,
  isUnhidingPost,
  onBlock,
  isBlocking,
  onUnblock,
  isUnblocking,
  onFacePost,
  isSettingFacePost,
  onRemovefacePost,
  isRemovingFacePost,
  item,
  mode,
  disabled,
}) => {
  const [openedLists, setOpenedLists] = useState({ reasons: true, comments: false });
  const [listComplaintReasons, setListComplaintReasons] = useState<ComplaintReason[]>([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const content = useTranslate(componentContent);
  const {
    list: listComplainers,
    preloaders: preloadersComplainers,
    pagination: paginationComplainers,
  } = useStore(spamComplainers);
  const { allowChange, allowModerate } = useGuards();

  const isLoadingComplainers = !!preloadersComplainers.getList;

  const listReasons = useMemo(() => {
    return listComplaintReasons.reduce((accumulator, complaintReason) => {
      const { id, type: text } = complaintReason;
      if (text) {
        accumulator.push({ id, text });
      }
      return accumulator;
    }, [] as { id: number; text: string }[]);
  }, [listComplaintReasons]);

  const listComments = useMemo(() => {
    return listComplaintReasons.reduce((accumulator, complaintReason) => {
      const { id, text } = complaintReason;
      if (text) {
        accumulator.push({ id, text });
      }
      return accumulator;
    }, [] as { id: number; text: string }[]);
  }, [listComplaintReasons]);

  const shadowbanButton = useMemo(() => {
    if (!(item as IPost).isChecked)
      return (
        <Button
          loading={isHidingPost}
          disabled={disabled}
          size='small'
          onClick={onHidePost}
        >
          {content.shadowban}
        </Button>
      );

    return !(item as IPost).isHidden ? (
      <Button
        loading={isHidingPost}
        disabled={disabled}
        size='small'
        onClick={onHidePost}
      >
        {content.shadowban}
      </Button>
    ) : (
      <Button
        loading={isUnhidingPost}
        disabled={disabled}
        size='small'
        onClick={onUnhidePost}
      >
        {content.unshadowban}
      </Button>
    );
  }, [
    content.shadowban,
    content.unshadowban,
    disabled,
    isHidingPost,
    isUnhidingPost,
    item,
    onHidePost,
    onUnhidePost,
  ]);

  const faceButton = useMemo(() => {
    return !(
      (item as IPost).marks?.length &&
      (item as IPost).marks?.filter((item) => item?.markType === 'FACE').length
    ) ? (
      <Button
        loading={isSettingFacePost}
        disabled={disabled}
        size='small'
        onClick={onFacePost}
      >
        {content.face}
      </Button>
    ) : (
      <Button
        loading={isRemovingFacePost}
        disabled={disabled}
        size='small'
        onClick={onRemovefacePost}
      >
        {content.notface}
      </Button>
    );
  }, [
    content.face,
    content.notface,
    disabled,
    isSettingFacePost,
    isRemovingFacePost,
    item,
    onFacePost,
    onRemovefacePost,
  ]);

  const handleOpenComplainers = useCallback(
    (value: boolean) => {
      if (value) {
        resetComplainers();
        getListComplainers({ itemId: item.id, mode });
      }
    },
    [item.id, mode],
  );

  const getListReasons = useCallback(
    async ({
      itemId,
      mode,
    }: {
      itemId: IPost['id'] | IComment['id'];
      mode: 'post' | 'comment';
    }) => {
      const hasCachedReasons = complaintReasonsCache.has(
        getComplaintReasonsCacheKey(itemId, mode),
      );
      if (!hasCachedReasons) setIsLoadingReasons(true);

      return await fetchComplaintReasons({ itemId, mode }).finally(() => {
        if (!hasCachedReasons) setIsLoadingReasons(false);
      });
    },
    [],
  );

  useEffect(() => {
    let isMounted = true;

    const cachedReasons = complaintReasonsCache.get(getComplaintReasonsCacheKey(item.id, mode));
    setListComplaintReasons(cachedReasons || []);
    setIsLoadingReasons(!cachedReasons);
    getListReasons({ itemId: item.id, mode }).then((response) => {
      if (isMounted) {
        setListComplaintReasons(response);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [getListReasons, item.id, mode]);

  const handleOpenReasons = useCallback(
    async (value) => {
      setOpenedLists({ ...openedLists, reasons: value });
      if (value && !openedLists.comments) {
        setListComplaintReasons([]);
        const response = await getListReasons({ itemId: item.id, mode });
        setListComplaintReasons(response);
      }
    },
    [getListReasons, item.id, mode, openedLists],
  );

  const handleOpenComments = useCallback(
    async (value) => {
      setOpenedLists({ ...openedLists, comments: value });
      if (value && !openedLists.reasons) {
        setListComplaintReasons([]);
        const response = await getListReasons({ itemId: item.id, mode });
        setListComplaintReasons(response);
      }
    },
    [getListReasons, item.id, mode, openedLists],
  );

  const handleChangePaginationComplainers = useCallback(
    (page: number) => {
      getListComplainers({ itemId: item.id, mode, pagination: { current: page } });
    },
    [item.id, mode],
  );

  return (
    <div className={css.root}>
      <div className={css.complaintsWrapper}>
        <div className={css.complaintsItem}>
          <span className={css.complaintsTitle}>{content.complained}:</span>{' '}
          <ComplaintsList
            parentEntityID={item.id}
            items={listComplainers}
            isListLoading={isLoadingComplainers}
            pagination={paginationComplainers}
            onOpen={handleOpenComplainers}
            onPagination={handleChangePaginationComplainers}
          >
            <span className='clickable'>
              {item.complainsCount ?? ('accountsComplained' in item && item.accountsComplained)}
            </span>
          </ComplaintsList>
        </div>
        <div className={css.complaintsItem}>
          <ComplaintsReasonsList
            classNameText={css.complaintsTitle}
            text={content.complaintReason}
            items={listReasons}
            isListLoading={isLoadingReasons}
            onOpen={handleOpenReasons}
            defaultOpened
          />
        </div>
        <div className={css.complaintsItem}>
          <ComplaintsReasonsList
            classNameText={css.complaintsTitle}
            text={content.complaintComment}
            items={listComments}
            isListLoading={isLoadingReasons}
            onOpen={handleOpenComments}
          />
        </div>
      </div>
      {allowChange || allowModerate ? (
        <div>
          <Button.Group>
            <Button
              loading={isBlocking}
              size='small'
              onClick={onBlock}
              disabled={disabled}
              type={item.isChecked && item.isSpamApproved && item.isBlocked ? 'primary' : 'default'}
            >
              {content.submitText}
            </Button>
            <Button
              loading={isUnblocking}
              disabled={disabled}
              size='small'
              onClick={onUnblock}
              type={
                item.isChecked && !item.isSpamApproved && !item.isBlocked ? 'primary' : 'default'
              }
            >
              {content.cancelText}
            </Button>
            {mode === 'post' && shadowbanButton}
            {faceButton}
          </Button.Group>
        </div>
      ) : null}
    </div>
  );
};

export default SpamPanel;
